/* eslint-disable import/max-dependencies */
import { createSelector } from "reselect";

import type { LoadingState } from "@fas/ui-framework";
import type { List } from "immutable";
import type { Page } from "../../components/PagesList/types/PagesList.types";
import type { State as PageInfoState } from "../../reducers/pageInfo";
import type { State as PageVersionsState, Version, VersionType } from "../../reducers/pageVersions";
import type { State as PageModifiersState } from "../../reducers/pageModifiers";
import type { State as PageResourcesState, Resource } from "../../reducers/pageResources";

import type { State } from "../../pages/AppStoreWrapper";
import type { State as PagesListState } from "../../reducers/pages";

import { makeModifier } from "../../reducers/pageModifiers/reducer";
import type { Modifier, ModifierRecord } from "../../reducers/pageModifiers/reducer";
import { initPageModifiersState } from "../../reducers/pageModifiers";

const pageListSelector = (state: State): PagesListState => state.page.pagesList;
const pageInfoSelector = (state: State): PageInfoState => state.page.pageInfo;

export const getPageList = createSelector(
  pageListSelector,
  (pagesList: PagesListState): Array<Page> => pagesList.get("data")
);

export const getPageInfo = createSelector(
  pageInfoSelector,
  (pageInfo: PageInfoState): PageInfoState => pageInfo
);

export const getPageId = createSelector(
  pageInfoSelector,
  (pageInfo: PageInfoState): string => pageInfo.get("pageId")
);

export const getPageVersionId = createSelector(
  pageInfoSelector,
  (pageInfo: PageInfoState): string => pageInfo.get("versionId")
);

export const getPageType = createSelector(
  pageInfoSelector,
  (pageInfo: PageInfoState): string => pageInfo.get("pageType")
);

export const getPageCurrentVersionType = createSelector(
  pageInfoSelector,
  (pageInfo: PageInfoState): VersionType => pageInfo.get("versionType")
);

export const getPageVersions = createSelector(
  (state: State): PageVersionsState => state.page.pageVersions,
  (pageVersions: PageVersionsState): PageVersionsState => pageVersions
);

export const getPageVersionByType = createSelector(
  (
    state: State,
    versionType: VersionType
  ): Version => state.page.pageVersions.get(versionType),
  (pageVersions: Version): Version => pageVersions
);

export const getDocumentModifiers = createSelector(
  (state: State) => state.page.pageModifiers,
  (pageModifiers: PageModifiersState): Array<Modifier> => pageModifiers.get("allIds")
    .map((key: string): Modifier => {
      const modifierRecord = pageModifiers.getIn(["byId", key]) || makeModifier();
      return (modifierRecord as ModifierRecord).toObject();
    }).toArray()
);

export const getDefaultModifiersName = () => {
  const state = initPageModifiersState();
  return state.get("allIds")
    .map((key: string): string => {
      const name = state.getIn(["byId", key, "name"]);
      return (name as string | undefined) || "";
    }).toArray();
};

export const getPageResources = createSelector(
  (state: State): PageResourcesState => state.page.pageResources,
  (pageResources: PageResourcesState): Array<Resource> => pageResources.get("resources")
);

export const getPagesListLoading = createSelector(
  (state: State): LoadingState => state.loading,
  (loading: LoadingState): boolean => loading.get("pagesListLoading", false)
);
